import { PRO_PLAN, SPARK_PLAN, STANDARD_PLAN } from '@/utils/data/plans'
import { CheckCircle, InfoTriangle, Upload, XCircle } from '@mynaui/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import Dropzone, { DropzoneState } from 'shadcn-dropzone'
import { useLocalStorage } from 'usehooks-ts'
import { Button } from './shadcn/button'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './shadcn/carousel'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './shadcn/dialog'
import { Progress } from './shadcn/progress'

export const UploadAdImage = ({
  onFileSelect,
  previewUrl,
  disabled,
  onRemove,
}: {
  onFileSelect: (file: File | undefined) => void
  previewUrl: string | undefined
  disabled?: boolean
  onRemove: () => void
}) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | undefined>(undefined)
  const [isPdf, setIsPdf] = useState<boolean>(false)

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      if (file.type === 'application/pdf') {
        setIsPdf(true)
        setLocalPreviewUrl(URL.createObjectURL(file))
      } else {
        setIsPdf(false)
        const url = URL.createObjectURL(file)
        setLocalPreviewUrl(url)
      }
      onFileSelect(file)
    }
  }

  // Update isPdf state when previewUrl changes
  useEffect(() => {
    if (previewUrl) {
      const isPdfUrl = previewUrl.toLowerCase().endsWith('.pdf')
      setIsPdf(isPdfUrl)
    }
  }, [previewUrl])

  return (
    <Dropzone
      onDrop={handleDrop}
      accept={{
        'image/*': ['.png', '.jpg', '.jpeg'],
      }}
      disabled={disabled}
      noKeyboard={true}
    >
      {(dropzone: DropzoneState) => (
        <div className="h-[30svh] lg:h-[60svh] w-full bg-gray-50 flex flex-col justify-center items-center">
          {previewUrl || localPreviewUrl ? (
            <div className="w-full h-[30svh] lg:h-[60svh] relative">
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-4">
                  <object data={previewUrl || localPreviewUrl} type="application/pdf" className="w-full h-full">
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <div className="w-16 h-16 mb-4">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-full h-full text-themedestructive"
                        >
                          <path
                            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2V8H20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 13H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 17H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 9H9H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-themeforeground">PDF Document</p>
                      <p className="text-xs text-thememutedforeground mt-1">Unable to preview PDF</p>
                    </div>
                  </object>
                </div>
              ) : (
                <img
                  src={previewUrl || localPreviewUrl}
                  alt="Preview"
                  className="w-full h-[30svh] lg:h-[60svh] object-contain"
                />
              )}
              <div className="absolute bottom-0 rounded-b-lg bg-gradient-to-t from-black/50 to-transparent w-full h-20 flex justify-center items-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-1/2 -translate-x-1/2 rounded-full bg-transparent border border-white text-white"
                  onClick={() => {
                    setLocalPreviewUrl(undefined)
                    setIsPdf(false)
                    onRemove()
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : dropzone.isDragAccept ? (
            <div className="text-sm font-medium">Drop your files here!</div>
          ) : (
            <div className="flex items-center flex-col text-sm font-medium text-thememutedforeground">
              <div className="w-[2.125rem] h-[2.125rem] rounded-full border-2 border-dashed flex justify-center items-center">
                <Upload />
              </div>
              <p className="">
                <span className="text-themedestructive">Upload files</span> or drag and drop
              </p>
              <p>PNG or JPG (max 10MB)</p>
            </div>
          )}
        </div>
      )}
    </Dropzone>
  )
}

export const ScoreMeasures = ({
  score,
  name,
  description,
}: {
  score: number
  name: string
  description: string
}) => {
  return (
    <div className="p-4 border rounded-xl space-y-3">
      <div className="flex justify-between gap-5">
        <div className="flex gap-3 items-center">
          <div className="w-8 h-8 rounded-full border flex justify-center items-center">
            {score >= 80 ? (
              <CheckCircle size={16} color="#28BE53BE" />
            ) : score > 40 ? (
              <InfoTriangle size={16} color="#FF8731" />
            ) : (
              <XCircle size={16} color="#FF0000" />
            )}
          </div>
          <div>
            <p className="text-sm font-semibold">
              {name
                .split('_')
                .join(' ')
                .split(' ')
                .map((word) => {
                  // Special case for 'cta' to make it uppercase
                  if (word.toLowerCase() === 'cta') {
                    return 'CTA'
                  }
                  return word.charAt(0).toUpperCase() + word.slice(1)
                })
                .join(' ')}
            </p>
          </div>
        </div>
        <div>
          <p
            className={`text-xs font-semibold ${score >= 80 ? 'text-[#28BE53BE]' : score > 40 ? 'text-[#FF8731]' : 'text-red-500'}`}
          >{`${score}/100`}</p>
        </div>
      </div>
      <div>
        <Progress value={score} color={score >= 80 ? 'bg-[#28BE53BE]' : score > 40 ? 'bg-[#FF8731]' : 'bg-red-500'} />
      </div>
      <div>
        <p className="text-sm font-medium text-thememutedforeground">{description}</p>
      </div>
    </div>
  )
}

export const FixesItem = ({
  heading,
  description,
  index,
}: {
  heading: string
  description: string
  index: number
}) => {
  return (
    <div className="p-4 border rounded-xl flex items-start gap-3">
      <div>
        <div className="w-8 h-8 text-sm font-bold rounded-full bg-green-50 flex justify-center items-center">
          <span>{index + 1}</span>
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold">{heading}</p>
        <p className="text-sm text-thememutedforeground">{description}</p>
      </div>
    </div>
  )
}

export function SignupPrompt({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [, set_cos_plan] = useLocalStorage<string>('cos_plan', '')
  const navigate = useNavigate()

  function checkoutPlan(selectedPlan: string) {
    if (selectedPlan) {
      set_cos_plan(selectedPlan) // Store selected plan in localStorage
      setTimeout(() => {
        navigate({ to: '/checkout' })
      }, 500)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="[&>button]:hidden max-w-[59.5rem]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{''}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="flex justify-center">
            <p className="text-2xl text-center font-semibold">
              Kick your <span className="text-themedestructive">creativity</span> into high gear with templates, tools,
              and inspiration.
            </p>
          </div>
          <div className="mb-3 mt-6">
            <h4 className="font-semibold">Available Plans</h4>
          </div>
          <div className="hidden lg:grid lg:grid-cols-3 gap-2.5">
            <SparkPlanCard checkoutPlan={checkoutPlan} />
            <StandardPlanCard checkoutPlan={checkoutPlan} />
            <ProPlanCard checkoutPlan={checkoutPlan} />
          </div>
          <div className="flex justify-center">
            <Carousel className="block lg:hidden md:mx-5 md:max-w-full max-w-xs ">
              <CarouselContent>
                <CarouselItem className="">
                  <SparkPlanCard checkoutPlan={checkoutPlan} />
                </CarouselItem>
                <CarouselItem className="">
                  <StandardPlanCard checkoutPlan={checkoutPlan} />
                </CarouselItem>
                <CarouselItem className="">
                  <ProPlanCard checkoutPlan={checkoutPlan} />
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="ml-5" />
              <CarouselNext className="mr-5" />
            </Carousel>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const SparkPlanCard = ({ checkoutPlan }: { checkoutPlan: (plan_id: string) => void }) => {
  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{SPARK_PLAN.name}</h3>
          </div>
          <p className={'mt-1'}>
            <span className={'text-2xl font-semibold'}>{SPARK_PLAN.price}</span> / mo. billed monthly
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{SPARK_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {SPARK_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
            {SPARK_PLAN.nonFeatures.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <XCircle className="w-4 h-4 text-red-600" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          checkoutPlan(SPARK_PLAN.plan_id)
        }}
        className="w-full mt-3"
        variant={'outline'}
      >
        {'Start Creating'}
      </Button>
    </div>
  )
}

const StandardPlanCard = ({ checkoutPlan }: { checkoutPlan: (plan_id: string) => void }) => {
  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{STANDARD_PLAN.name}</h3>
          </div>
          <p className={'mt-1'}>
            <span className={'text-2xl font-semibold'}>{STANDARD_PLAN.price}</span> / mo. billed monthly
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{STANDARD_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {STANDARD_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          checkoutPlan(STANDARD_PLAN.plan_id)
        }}
        className="w-full mt-3"
        variant={'outline'}
      >
        {'Start Free Trial'}
      </Button>
    </div>
  )
}

const ProPlanCard = ({ checkoutPlan }: { checkoutPlan: (plan_id: string) => void }) => {
  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full bg-gradient-to-bl from-purple-100 via-white to-white'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{PRO_PLAN.name}</h3>
          </div>
          <p>
            <span className={'text-2xl font-semibold'}>{PRO_PLAN.price}</span> / mo. billed monthly
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{PRO_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {PRO_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          checkoutPlan(PRO_PLAN.plan_id)
        }}
        className="w-full mt-3"
        variant={'destructivePurple'}
      >
        {'Get Pro'}
      </Button>
    </div>
  )
}
